import { v4 as uuidv4 } from 'uuid'
import { computeBoundingBox } from './boundingBox'

/**
 * Creates a group from selected items.
 * @param {Array} selectedItems - Items to group (must be >= 2)
 * @param {Array} allItems - All page-level items
 * @returns {{ group: Object, updatedItems: Array }} The new group and updated items array
 */
export function createGroup(selectedItems, allItems) {
  if (!selectedItems || selectedItems.length < 2) {
    return { group: null, updatedItems: allItems }
  }

  // 1. Compute bounding box of selected items
  const bbox = computeBoundingBox(selectedItems)

  // 2. Set group position and dimensions from bounding box
  const groupX = bbox.minX
  const groupY = bbox.minY
  const groupWidth = bbox.width
  const groupHeight = bbox.height

  // 3. Convert each child's absolute coords to relative coords
  const children = selectedItems.map(item => ({
    ...item,
    x: item.x - groupX,
    y: item.y - groupY
  }))

  // 4. Compute layer as max layer among selected items
  const maxLayer = Math.max(...selectedItems.map(item => item.layer || 0))

  // 5. Compute time to span from earliest start to latest end
  const starts = selectedItems.map(item => {
    if (item.time && typeof item.time.start === 'number') {
      return item.time.start
    }
    return 0
  })
  const ends = selectedItems.map(item => {
    if (item.time && typeof item.time.start === 'number' && typeof item.time.duration === 'number') {
      return item.time.start + item.time.duration
    }
    return 0
  })
  const minStart = Math.min(...starts)
  const maxEnd = Math.max(...ends)

  // 6. Build the group item
  const group = {
    id: `group-${uuidv4()}`,
    type: 'group',
    x: groupX,
    y: groupY,
    width: groupWidth,
    height: groupHeight,
    rotation: 0,
    opacity: 1,
    layer: maxLayer,
    locked: false,
    time: {
      start: minStart,
      duration: maxEnd - minStart
    },
    children
  }

  // 7. Remove selected items from allItems and insert the group
  const selectedIds = new Set(selectedItems.map(item => item.id))
  const updatedItems = allItems.filter(item => !selectedIds.has(item.id))
  updatedItems.push(group)

  return { group, updatedItems }
}

/**
 * Dissolves a group, restoring children to the top-level items array.
 * @param {Object} groupItem - The group to ungroup
 * @param {Array} allItems - All page-level items
 * @returns {Array} Updated items array with group removed and children restored
 */
export function ungroupItem(groupItem, allItems) {
  if (!groupItem || groupItem.type !== 'group') {
    return allItems
  }

  // Convert each child's relative coords back to absolute coords
  const restoredChildren = (groupItem.children || []).map(child => ({
    ...child,
    x: child.x + groupItem.x,
    y: child.y + groupItem.y
  }))

  // Find the group's position in the array
  const groupIndex = allItems.findIndex(item => item.id === groupItem.id)
  if (groupIndex === -1) {
    return allItems
  }

  // Remove the group and insert restored children at the group's position
  const updatedItems = [...allItems]
  updatedItems.splice(groupIndex, 1, ...restoredChildren)

  return updatedItems
}

/**
 * Deep-clones an item and assigns fresh ids to it and (recursively) to any
 * group children or PowerClip contents. Used when duplicating/pasting so the
 * copy does not share ids with the original (which would break selection,
 * updates, and deletion).
 * @param {Object} item - The item to clone
 * @returns {Object} A structurally-cloned item with new ids throughout
 */
export function cloneItemWithNewIds(item) {
  if (!item || typeof item !== 'object') return item

  const typePrefix = item.type || 'item'
  const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const cloned = { ...item, id: `${typePrefix}-${uniqueSuffix}` }

  // Recurse into group children.
  if (Array.isArray(item.children)) {
    cloned.children = item.children.map(cloneItemWithNewIds)
  }

  // Recurse into PowerClip contents, keeping the back-pointer consistent.
  if (Array.isArray(item.powerClipContents)) {
    cloned.powerClipContents = item.powerClipContents.map(child => {
      const c = cloneItemWithNewIds(child)
      if (child.powerClipId !== undefined) c.powerClipId = cloned.id
      return c
    })
  }

  return cloned
}

/**
 * Applies a single property update to EVERY child of a group (used to apply one
 * animation/effect to all grouped items at once). Children stay relative.
 * @param {Object} groupItem - The group whose children to update
 * @param {string} property - The property name to set
 * @param {*} value - The value to set
 * @returns {Object} Updated group with the property applied to all children
 */
export function applyPropertyToGroupChildren(groupItem, property, value) {
  if (!groupItem || groupItem.type !== 'group' || !Array.isArray(groupItem.children)) {
    return groupItem
  }
  return {
    ...groupItem,
    children: groupItem.children.map(child => ({ ...child, [property]: value }))
  }
}

/**
 * Scales all children proportionally when the group is resized.
 * @param {Object} groupItem - The group being resized
 * @param {number} scaleX - Horizontal scale factor
 * @param {number} scaleY - Vertical scale factor
 * @returns {Object} Updated group with scaled children
 */
export function resizeGroup(groupItem, scaleX, scaleY) {
  if (!groupItem || groupItem.type !== 'group') {
    return groupItem
  }

  const scaledChildren = (groupItem.children || []).map(child => ({
    ...child,
    x: child.x * scaleX,
    y: child.y * scaleY,
    width: child.width * scaleX,
    height: child.height * scaleY
  }))

  return {
    ...groupItem,
    width: groupItem.width * scaleX,
    height: groupItem.height * scaleY,
    children: scaledChildren
  }
}
